import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-scroll";
import { useTranslation } from "react-i18next";
import { QRCodeCanvas } from "qrcode.react";
import { FaDesktop, FaMobileAlt, FaAndroid } from "react-icons/fa";

const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isTabletOrPhone, setIsTabletOrPhone] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    const mediaQuery = window.matchMedia("(max-width: 1024px)");
    setIsTabletOrPhone(mediaQuery.matches);

    const handleResize = () => setIsTabletOrPhone(mediaQuery.matches);
    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleLinkClick = () => {
    setIsOpen(false);
  };

  // QR Code Links
  const androidAppLink = "https://play.google.com/store";

  return (
    <div>
      <input
        type="checkbox"
        id="navi-toggle"
        className="hidden"
        checked={isOpen}
        onChange={() => setIsOpen(!isOpen)}
      />

      <motion.div
        className="fixed top-0 left-0 w-full h-full bg-gradient-to-br from-primary-light to-primary-dark z-[1000] rounded-full"
        initial={false}
        animate={isOpen ? "open" : "closed"}
        variants={{
          open: {
            scale: 80,
            opacity: 1,
            transition: { duration: 0.8, ease: [0.86, 0, 0.07, 1] },
          },
          closed: {
            scale: 0,
            opacity: 0,
            transition: { duration: 0.8, ease: [0.86, 0, 0.07, 1] },
          },
        }}
      />

      <label
        htmlFor="navi-toggle"
        className={`fixed z-[2000] top-8 right-8 m-2 w-14 h-14 bg-white rounded-full shadow-[0_1rem_2.5rem_rgba(0,0,0,0.1)] flex items-center 
            justify-center cursor-pointer transition-all`}
      >
        <motion.div
          className="relative w-10 h-[2px] bg-gray-800"
          initial={false}
          animate={isOpen ? "open" : "closed"}
          variants={{
            open: {
              backgroundColor: "transparent",
              transition: { duration: 0.2 },
            },
            closed: {
              backgroundColor: "#1a202c",
              transition: { duration: 0.2 },
            },
          }}
        >
          <motion.span
            className="absolute top-[-7px] left-0 w-full h-[2px] bg-gray-800"
            variants={{
              open: { top: 0, rotate: 45 },
              closed: { top: "-7px", rotate: 0 },
            }}
          ></motion.span>
          <motion.span
            className="absolute top-[7px] left-0 w-full h-[2px] bg-gray-800"
            variants={{
              open: { top: 0, rotate: -45 },
              closed: { top: "7px", rotate: 0 },
            }}
          ></motion.span>
        </motion.div>
      </label>

      <motion.nav
        className="fixed top-0 left-0 h-screen w-screen bg-green-950 z-[1500]"
        initial={false}
        animate={isOpen ? "open" : "closed"}
        variants={{
          open: {
            opacity: 1,
            width: "100%",
            pointerEvents: "auto",
            visibility: "visible",
            transition: { duration: 0.8 },
          },
          closed: {
            opacity: 0,
            width: "0",
            pointerEvents: "none",
            visibility: "hidden",
            transition: { duration: 0.8 },
          },
        }}
      >
        <ul className="fixed w-full top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-center list-none">
          <li className="my-[4vh]">
            <Link
              to="solutions" // Use the section id as the target
              smooth={true}
              duration={500}
              offset={-100}
              className="md:text-3xl xxs:text-xl sm:text-2xl font-light text-white uppercase no-underline transition-all hover:bg-gradient-to-r hover:from-transparent 
              hover:to-white hover:bg-[length:220%] hover:bg-pos-right hover:translate-x-4 cursor-pointer"
              onClick={handleLinkClick} // Close menu when link is clicked
            >
              {t("navigation.solutions")}
            </Link>
          </li>
          <li className="my-[4vh]">
            <Link
              to="about"
              smooth={true}
              duration={500}
              className="md:text-3xl xxs:text-xl sm:text-2xl font-light text-white uppercase no-underline transition-all hover:bg-gradient-to-r hover:from-transparent 
              hover:to-white hover:bg-[length:220%] hover:bg-pos-right hover:translate-x-4 cursor-pointer"
              onClick={handleLinkClick} // Close menu when link is clicked
            >
              {t("navigation.about")}
            </Link>
          </li>
          <li className="my-[4vh]">
            <Link
              to="news"
              smooth={true}
              duration={500}
              offset={110}
              className="md:text-3xl xxs:text-xl sm:text-2xl font-light text-white uppercase no-underline transition-all hover:bg-gradient-to-r hover:from-transparent 
              hover:to-white hover:bg-[length:220%] hover:bg-pos-right hover:translate-x-4 cursor-pointer"
              onClick={handleLinkClick} // Close menu when link is clicked
            >
              {t("navigation.news")}
            </Link>
          </li>
          <li className="my-[4vh]">
            <Link
              to="gallery"
              smooth={true}
              duration={500}
              offset={100}
              className="md:text-3xl xxs:text-xl sm:text-2xl font-light text-white uppercase no-underline transition-all hover:bg-gradient-to-r hover:from-transparent 
              hover:to-white hover:bg-[length:220%] hover:bg-pos-right hover:translate-x-4 cursor-pointer"
              onClick={handleLinkClick} // Close menu when link is clicked
            >
              {t("navigation.gallery")}
            </Link>
          </li>
          <li className="my-[4vh]">
            <Link
              to="tenders"
              smooth={true}
              duration={500}
              className="md:text-3xl xxs:text-xl sm:text-2xl font-light text-white uppercase no-underline transition-all hover:bg-gradient-to-r hover:from-transparent 
              hover:to-white hover:bg-[length:220%] hover:bg-pos-right hover:translate-x-4 cursor-pointer"
              onClick={handleLinkClick} // Close menu when link is clicked
            >
              {t("navigation.tenders")}
            </Link>
          </li>
          <li className="my-[4vh]">
            <Link
              to="contact"
              smooth={true}
              duration={500}
              className="md:text-3xl xxs:text-xl sm:text-2xl font-light text-white uppercase no-underline transition-all hover:bg-gradient-to-r hover:from-transparent 
              hover:to-white hover:bg-[length:220%] hover:bg-pos-right hover:translate-x-4 cursor-pointer"
              onClick={handleLinkClick} // Close menu when link is clicked
            >
              {t("navigation.contact")}
            </Link>
          </li>
        </ul>

        <div className="fixed bottom-8 left-8 flex flex-col space-y-4 z-[2000] xxs:text-xs md:text-base">
  <a
    href="https://smlepenec.ddnsfree.com"
    target="_blank"
    rel="noopener noreferrer"
    className="flex items-center px-4 py-2 bg-white text-green-950 rounded-full shadow-md hover:bg-gray-300 transition-all"
  >
    <FaDesktop className="mr-2" />
    {t("navigation.webApp")}
  </a>

  <a
    href={androidAppLink}
    target="_blank"
    rel="noopener noreferrer"
    className="flex items-center px-4 py-2 bg-white text-green-950 rounded-full shadow-md hover:bg-gray-300 transition-all"
  >
    <FaMobileAlt className="mr-2" />
    {t("navigation.mobileApp")}
  </a>

  {!isTabletOrPhone && (
    <div className="flex space-x-4">
      <div className="flex flex-col items-center">
        <QRCodeCanvas
          value={androidAppLink}
          size={128}
          bgColor="#ffffff"
          fgColor="#000000"
          level="Q"
        />
       <p className="mt-2 text-white flex items-center">
        <FaAndroid className="mr-2" />
          Android
        </p>
      </div>
    </div>
  )}
</div>
      </motion.nav>
    </div>
  );
};

export default Navigation;